'use client';
import { useForm } from 'react-hook-form';
import { useAction } from 'next-safe-action/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight } from 'lucide-react';
import { z } from 'zod';
import { completeProfileAction } from '~/lib/accounts/profile-actions';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Spinner } from '~/components/ui/spinner';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form';

const schema = z.object({ name: z.string().trim().min(1, 'Enter your name').max(100) });

/** Asks a new person for the name shown on their profile. */
export function NameStep({ onDone }: { onDone: () => void }) {
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { name: '' } });
  const action = useAction(completeProfileAction, { onSuccess: () => onDone() });
  return (
    <Form {...form}>
      <form
        className="space-y-5"
        onSubmit={form.handleSubmit(action.execute)}
        data-test="onboarding-name-form"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>What should we call you?</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  autoComplete="name"
                  autoFocus
                  placeholder="Your name"
                  readOnly={action.isPending}
                  data-test="onboarding-name-input"
                />
              </FormControl>
              <FormDescription>This is the name shown on your profile.</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        {action.result.serverError && (
          <p role="alert" className="text-destructive text-sm">
            {action.result.serverError}
          </p>
        )}
        <Button
          type="submit"
          className="w-full"
          disabled={action.isPending}
          data-test="onboarding-name-submit"
        >
          {action.isPending ? <Spinner /> : null}
          Continue
          {!action.isPending && <ArrowRight className="size-4" />}
        </Button>
      </form>
    </Form>
  );
}
